import { parseDomainObjToString, parseDomainStringToObj } from 'helpers/domain';
import {
  ModesResponderEnum,
  SanitizingTypeEnum,
  TrackingVendorsEnum,
  TypeResponderOfferEnum,
  sendMethodEnum,
  stateOffer,
} from 'consts/data';
import { isArray, isString, cloneDeep, get, isEmpty, set, isPlainObject } from 'lodash';
import CampaignModel from 'models/campaign.model';
import { isDefine, removeNullOfObject } from 'helpers';
import MediaModel from 'models/media.model';
import OptOutModel from 'models/optout.model';
import { calculateIVR, calculateRevenueCampaign, costCalculator } from './calculate';

export const parseSuppressionContactGroup = (values) => {
  return {
    suppressionContactEnabled: values?.suppressionContactEnabled,
    suppressionGroupIds: isArray(values?.suppressionGroupIds)
      ? values?.suppressionGroupIds?.map((el) => el.value)
      : [],
  };
};

export const collectRawStats = (stats) => {
  return {
    ...stats,

    total: stats?.total || 0,
    sent: stats?.sent || 0,
    delivered: stats?.delivered || 0,
    failed: stats?.failed || 0,
    blocked: stats?.block || stats?.blocked || 0,
    replied: stats?.replied || 0,
    optout: stats?.optout || 0,
    clicked: stats?.clicked || 0,
    attemptFailed: stats?.attemptFailed || 0,
    convertor: stats?.convertor || 0,
    callback: stats?.callback || 0,
    profit: (stats?.revenue || 0) - (stats?.cost || 0),

    //* For response history
    sentSoFar: stats?.sentSoFar || 0,
    deliveredCount: stats?.deliveredCount || 0,
    noOfFail: stats?.noOfFail || 0,
    blockedCount: stats?.blockedCount || 0,
    clickCounter: stats?.clickCounter || 0,
    totalDid: stats?.totalDid || 0,
    remainContact: stats?.remainContact || 0,
    callbackCount: stats?.callbackCount || 0,
    listenCounter: stats?.listenCounter || 0,

    //* ACD
    callDuration: stats?.callDuration || 0,
    callDurationInMinute: stats?.callDurationInMinute || 0,
    callDurationInSecond: stats?.callDurationInSecond || 0,

    //* IVR Clicked + Optout
    ivrClickRate: stats.ivrClickRate,
    ivrOptoutRate: stats.ivrOptoutRate,
  };
};

export const parseStatsToCalculate = (el) => {
  const callDurationSecond = isNaN(
    (el?.callDurationInSecond || 0) / (el?.deliveredCount || el?.delivered || 0)
  )
    ? 0
    : (el?.callDurationInSecond || 0) / (el?.deliveredCount || el?.delivered || 0);

  const clicked = el?.clickCounter || el?.clicked || 0;
  const delivered = el?.delivered || el?.deliveredCount || 0;
  const callback = el?.callbackCount || el?.callback || el?.callbackCounter || 0;
  const sent = el?.sent || el?.sentSoFar || 0;
  const blocked = el?.blockedCount || el?.blocked || el?.block || 0;
  const failed = el?.noOfFail || el?.failed || 0;
  const convertor = el?.convertorCount || el?.convertor || 0;
  const listen = el?.listenCounter || el?.listen || 0;

  const { roi, revPerMil, revPerMilMessage, revPerMilMinute, profit, revPerCall } =
    calculateRevenueCampaign({
      revenue: el?.revenue,
      cost: el?.cost,
      delivered: delivered,
      sent: sent,
      inboundIvrClickDurationSec: el?.inboundIvrClickDurationSec,
      outboundIvrClickDurationSec: el?.outboundIvrClickDurationSec,
    });

  return {
    total: el?.total || 0,
    sent: sent,
    delivered: delivered,
    deliveredPerSent: delivered / sent,

    failed: failed,
    failedPerSent: failed / sent,

    block: blocked,
    blocked: blocked,
    blockedPerSent: blocked / sent,

    replied: el?.replied || 0,
    repliedPerDelivered: el?.replied / delivered,

    optout: el?.optout || 0,
    optoutPerSent: el?.optout / sent,

    clicked: clicked,
    clickedPerDelivered: clicked / delivered,

    attemptFailed: el?.attemptFailed || 0,

    convertor: convertor,
    convertorPerDelivered: convertor / delivered,

    callback: callback,
    callbackPerDelivered: callback / delivered,

    listenCounter: listen || 0,
    listenCounterPerDelivered: listen / delivered,

    rejected: el?.rejected || 0,
    rejectedPerSent: el?.rejected / sent,

    acd: callDurationSecond,
    callBlocks: el?.callDurationInMinute || 0,
    amd: el?.amd || 0,
    ivrClickRate: calculateIVR.ivrClickRate({
      outboundIvrClickThrough: el?.outboundIvrClickThrough,
      inboundIvrClickThrough: el?.inboundIvrClickThrough,
      callback: callback,
      delivered: delivered,
    }),
    ivrOptoutRate: calculateIVR.ivrOptoutRate({
      outboundIvrOptout: el?.outboundIvrOptout,
      inboundIvrOptout: el?.inboundIvrOptout,
      callback: callback,
      delivered: delivered,
    }),
    cost: el?.cost,
    costPerMessage: costCalculator.costPerMessage({
      cost: el?.cost,
      revenue: el?.revenue,
      sent: el?.sent,
      isReturnNumber: true,
    }),
    costPerClicked: costCalculator.costPerClicked({
      clicked: clicked,
      cost: el?.cost,
      isReturnNumber: true,
    }),

    roi,
    revPerMil,
    revPerMilMessage,
    revPerMilMinute,
    profit,
    revPerCall,
    revenue: el?.revenue,
    revenuePerMil: el?.revenuePerMil,

    callDurationInSecond: el?.callDurationInSecond,
    amdCost: el?.amdCost,
    callRate: el?.callRate,
  };
};

export const parseIVROutbound = (ivrSetting = {}) => {
  if (isEmpty(ivrSetting)) {
    return {};
  }

  const nextIVRSetting = {
    ...cloneDeep(ivrSetting),
    forwardPhone: ivrSetting?.forwardPhone || '',
    ivrMediaId: ivrSetting?.ivrMediaId?.id,
    noanswerMediaId: ivrSetting?.noanswerMediaId?.id,
    audioBuilderSettings: {
      ...ivrSetting?.audioBuilderSettings,
      tagsString: isString(ivrSetting?.audioBuilderSettings?.tagsString)
        ? CampaignModel.parseTagsStringFromResponse(ivrSetting?.audioBuilderSettings?.tagsString)
        : ivrSetting?.audioBuilderSettings?.tagsString,
    },
  };

  const { audioBuilderEnabled, audioBuilderSettings } = nextIVRSetting || {};

  if (audioBuilderEnabled) {
    const mainAudioId = audioBuilderSettings?.mainAudioId?.value;
    const tagsString = audioBuilderSettings?.tagsString;
    const enableMediaGroup = audioBuilderSettings?.enableMediaGroup;

    nextIVRSetting.ivrMediaId = null;
    nextIVRSetting.noanswerMediaId = null;

    if (enableMediaGroup) {
      nextIVRSetting.audioBuilderSettings = {
        mediaGroupId: nextIVRSetting?.audioBuilderSettings?.mediaGroupId?.id,
        // tagsString: '[Main Audio]',
        tagsString: isArray(tagsString) ? tagsString.map((el) => el.content).join('') : '',
      };
    } else {
      nextIVRSetting.audioBuilderSettings = {
        mainAudioId,
        tagsString: isArray(tagsString) ? tagsString.map((el) => el.content).join('') : '',
      };
    }
  } else {
    nextIVRSetting.audioBuilderSettings = null;
  }

  return nextIVRSetting;
};

/**
 *
 * @param {[{ ani: ''}]} anis
 * @returns
 */
export const parseANIS = (anis) => {
  const result = {
    anis: [],
    routeAnis: {},
  };

  if (isArray(anis)) {
    anis.forEach((el) => {
      const { routes, ani } = el;
      result.anis.push({ ani });

      if (isArray(routes)) {
        routes.forEach((route) => {
          result.routeAnis[route?.id] = ani;
        });
      }
    });
  }

  return result;
};

export const parseAutoOptimization = (values) => {
  const totalTarget = values?.totalTarget;
  let {
    autoOp,
    autoOpLap2,
    testSize,
    pausedMin,
    threshold,
    autoOpLap2CreativeSize,

    thresholdPerReply,
    thresholdPerBlock,
    thresholdPerOptout,
    thresholdPerConvertor,

    thresholdRevenuePerMilMessage,
    thresholdRevenuePerMilMinute,
    thresholdRevenuePerDeliveredCall,
    thresholdCallRate,
    thresholdRoi,

    testSizeLap2,
    pausedMinLap2,
    thresholdLap2,
    thresholdPerReplyLap2,
    thresholdPerBlockLap2,
    thresholdPerOptoutLap2,
    thresholdPerConvertor2,

    thresholdRevenuePerMilMessageLap2,
    thresholdRevenuePerMilMinuteLap2,
    thresholdRevenuePerDeliveredCallLap2,
    thresholdCallRateLap2,
    thresholdRoiLap2,
  } = values?.autoOptimization || {};

  //* Auto optimization
  if (!totalTarget || (totalTarget && Number(totalTarget) <= 99)) {
    autoOp = false;
    autoOpLap2 = false;
  }

  if (!autoOp) {
    testSize = '';
    pausedMin = '';
    threshold = '';
    autoOpLap2CreativeSize = '';
    thresholdPerReply = '';
    thresholdPerBlock = '';
    thresholdPerOptout = '';
    thresholdPerConvertor = '';

    thresholdRevenuePerMilMessage = '';
    thresholdRevenuePerMilMinute = '';
    thresholdRevenuePerDeliveredCall = '';
    thresholdCallRate = '';
    thresholdRoi = '';

    autoOpLap2 = false;
  }

  if (!autoOpLap2) {
    testSizeLap2 = '';
    pausedMinLap2 = '';
    thresholdLap2 = '';
    thresholdPerReplyLap2 = '';
    thresholdPerBlockLap2 = '';
    thresholdPerOptoutLap2 = '';
    thresholdPerConvertor2 = '';

    thresholdRevenuePerMilMessageLap2 = '';
    thresholdRevenuePerMilMinuteLap2 = '';
    thresholdRevenuePerDeliveredCallLap2 = '';
    thresholdCallRateLap2 = '';
    thresholdRoiLap2 = '';
  }

  return {
    // Lap 1
    autoOp,
    testSize,
    pausedMin,
    threshold,
    autoOpLap2CreativeSize,
    thresholdPerReply,
    thresholdPerBlock,
    thresholdPerOptout,
    thresholdPerConvertor,

    thresholdRevenuePerMilMessage,
    thresholdRevenuePerMilMinute,
    thresholdRevenuePerDeliveredCall,
    thresholdCallRate,
    thresholdRoi,

    // Lap 2
    autoOpLap2,
    testSizeLap2,
    pausedMinLap2,
    thresholdLap2,
    thresholdPerReplyLap2,
    thresholdPerBlockLap2,
    thresholdPerOptoutLap2,
    thresholdPerConvertor2,

    thresholdRevenuePerMilMessageLap2,
    thresholdRevenuePerMilMinuteLap2,
    thresholdRevenuePerDeliveredCallLap2,
    thresholdCallRateLap2,
    thresholdRoiLap2,

    // New (redesigned) optimization model: dynamic laps, each with its own
    // condition builder. The legacy flat fields above are kept for back-compat.
    laps: (values?.autoOptimization?.laps || []).map((lap) => ({
      testSize: Number(lap?.testSize) || 0,
      pausedMinutes: Number(lap?.pausedMinutes) || 0,
      selectTop: Number(lap?.selectTop) || 0,
      elements: (lap?.elements || []).map((el) => el?.value ?? el),
      conditions: (lap?.conditions || [])
        .filter((c) => c && c.condition && c.operator && `${c.value}` !== '')
        .map((c) => ({ condition: c.condition, operator: c.operator, value: Number(c.value) })),
    })),
  };
};

export const parseResponderOffer = (values) => {
  const { responderAdvanceSetting } = cloneDeep(values || {});
  return {
    responderOffers: (responderAdvanceSetting || []).map((el) => {
      if (el.typeInput === TypeResponderOfferEnum.selectOffer) {
        return {
          variable: el?.variable,
          offerId: el?.offerId?.value || '',
        };
      }

      if (el.typeInput === TypeResponderOfferEnum.onFly) {
        return {
          variable: el?.variable,
          url: el?.url,
          domainGroups: (el?.domain || []).map((el) => {
            return {
              groupName: el?.label,
              groupId: el?.value,
            };
          }),
        };
      }

      return {};
    }),
  };
};

export const parseResponderForwardPhones = (values) => {
  const { responderForwardPhones, modeResponder } = cloneDeep(values || {});
  const isResponderOnFly = modeResponder === ModesResponderEnum.onfly;

  return {
    responderForwardPhones: isResponderOnFly
      ? []
      : responderForwardPhones
          .filter((el) => !!el?.variable)
          .map((el) => {
            const { variable, phone } = el;
            return {
              variable,
              phone,
            };
          }) || [],
  };
};

export const parseOffers = (values) => {
  const tempValues = cloneDeep(values || {});
  const { offerIds, offerOnFly, offerOption } = tempValues || {};
  const domain = (values.domain || []).map((el) => el.id).join(',');

  let offers = [
    ...(offerIds || []).map((offer) => ({
      id: offer.id,
      url: '',
      domain: '',
      ppc: offer?.['ppc'],
    })),
    ...(offerOnFly || []).map((offer) => ({
      id: '',
      url: offer.url,
      domain: parseDomainObjToString(offer?.domain),
      ppc: offer?.['ppc'],
    })),
    //* For update
    ...(tempValues?.offers || []).map((offer) => {
      //* Existing
      if (offer.isFixed) {
        return {
          id: offer.offerId,
          url: '',
          domain: '',
          ppc: offer?.['ppc'],
        };
      }

      return {
        id: offer.id,
        url: '',
        domain: '',
        ppc: offer?.['ppc'],
      };
    }),
  ];

  //* Check offer state:
  if (offerOption === stateOffer.PREFILL_OFFER) {
    offers = [...offers, { id: '', url: '', domain }];
  }

  if (offerOption === stateOffer.NO_OFFER) {
    offers = [];
  }

  return {
    offers,
  };
};

export const parseCreatives = (values) => {
  const tempValues = cloneDeep(values || {});
  const { creativeIds, creativeOnFly } = tempValues;

  const isVoiceType = CampaignModel.isVoiceType(values.messageType);
  const agent = get(values, 'agent');

  let creatives = [
    ...(creativeIds || []).map((creative) => ({ id: creative.id, message: '' })),
    ...(creativeOnFly || []).map((creative) => ({
      id: '',
      message: creative.message,
    })),
    //* For update
    ...(tempValues?.creatives || []).map((creative) => {
      //* Existing
      if (creative.isFixed || isDefine(creative.creativeId)) {
        return {
          id: creative.creativeId,
          message: creative?.message,
        };
      }

      return {
        id: creative.id,
        message: creative?.message,
      };
    }),
  ];

  if (isVoiceType && !agent) {
    creatives = [];
  }

  return {
    creatives,
  };
};

export const parseRoutes = (values) => {
  const { routesOneWay, routesTwoWays, sendMethod } = cloneDeep(values || {});

  let routes = [];
  if (sendMethod === sendMethodEnum.oneWay) {
    routes = (routesOneWay || []).map((el) => ({
      routeId: el?.id,
      status: true,
      tenDLC: !!el?.is10dlc,
    }));
  }

  if (sendMethod === sendMethodEnum.twoWays) {
    routes = (routesTwoWays || []).map((el) => ({
      routeId: el?.routeId,
      didGroupIds: (get(el, 'didGroupIds') || []).map((didGroup) => didGroup?.value),
      status: true,
      tenDLC: !!el?.is10dlc,
    }));
  }

  return {
    routes,
  };
};

export const parsePopulateGroup = (values) => {
  const nextPopulateDataSetting = {};
  Object.entries(removeNullOfObject(values?.populateDataSetting || {})).forEach(
    ([typeGroup, group]) => {
      set(nextPopulateDataSetting, `${typeGroup}`, { groupId: group?.value || group?.id });
    }
  );

  return {
    //* Populate contact group
    populateDataSetting: nextPopulateDataSetting,
  };
};

export const initialResponderOffers = (listResponderOffer) => {
  if (listResponderOffer && typeof listResponderOffer === 'object') {
    return listResponderOffer.map((el) => ({
      ...el,
      typeInput: 'inputText',
      isFixed: true,
      domain: parseDomainStringToObj(el.domain, el.domains),
    }));
  }
  return [];
};

export const parseResponderOnfly = (values) => {
  const { responderOnFly, modeResponder } = cloneDeep(values);

  const { useConfigAudio, audioBuilderSetting, media } =
    MediaModel.parseBodyRequestForMediaWithConfigs(responderOnFly?.inboundMediaWithConfigs);

  const mediaHandler = get(responderOnFly, 'inboundMediaWithConfigs') || {};
  const mediaNotSelected =
    !isDefine(mediaHandler?.media) &&
    !isDefine(mediaHandler?.mainAudioId) &&
    !isDefine(mediaHandler?.mediaGroupId);

  return {
    responderOnFly:
      modeResponder !== ModesResponderEnum.onfly
        ? null
        : {
            inboundForwardNumber: responderOnFly?.inboundForwardNumber || '',
            outboundForwardNumber: responderOnFly?.outboundForwardNumber || '',
            inboundPlayAudioSetting: mediaNotSelected
              ? undefined
              : {
                  media: useConfigAudio
                    ? undefined
                    : {
                        id: media?.value,
                      },
                  audioBuilderSetting: useConfigAudio ? audioBuilderSetting : undefined,
                },
          },
  };
};

export const parseResponderAndTemplate = (values) => {
  let responderTemplate = '';
  let responderId = '';

  if (values?.modeResponder === ModesResponderEnum.responder) {
    responderId = isPlainObject(values?.responderId)
      ? values?.responderId?.value
      : values?.responderId || '';
  }

  if (values?.modeResponder === ModesResponderEnum.template) {
    responderTemplate = isPlainObject(values?.responderTemplate)
      ? values?.responderTemplate?.value
      : values?.responderTemplate || '';
  }

  return {
    //* Responder template + Responder V2
    responderTemplate,
    responderId,
  };
};

export const parseTrackingVendor = (values) => {
  const { trackingVendor } = values;
  let voluumSetting = {
    uuid: '',
    campaignName: '',
  };

  if (trackingVendor === TrackingVendorsEnum.Voluum) {
    voluumSetting = values?.voluumSetting;
  }

  return {
    trackingVendor: trackingVendor || undefined,
    voluumSetting: voluumSetting,
  };
};

export const parseMedia = (values) => {
  const isSMS = CampaignModel.isSMS(values.messageType);
  if (isSMS) {
    return {};
  }

  const mediaWithConfigs = MediaModel.parseBodyRequestForMediaWithConfigs(values?.mediaWithConfigs);
  const agent = values?.agent;

  let media = values?.media || [];
  if (!isEmpty(mediaWithConfigs?.media)) {
    media = mediaWithConfigs?.media;
  }

  return {
    //* TA-11166
    media: agent ? [] : media,
    audioBuilderSettings: agent ? null : mediaWithConfigs?.audioBuilderSetting,
    numberOfAttachmentInMessage: values?.numberOfAttachmentInMessage || 1,
  };
};

export const parseOptoutLink = (values) => {
  const isOneWay = CampaignModel.isOneWaySendMethod(values?.sendMethod);
  const isSMS = CampaignModel.isSMS(values.messageType);
  const isMMS = CampaignModel.isMMS(values.messageType);
  const isMMSE = CampaignModel.isMMSE(values.messageType);

  const optoutLink = values?.optoutLink || {};
  const { optoutLinkType, optoutLink: optoutLinkArr } = optoutLink || {};

  if (!isOneWay || (!isSMS && !isMMS && !isMMSE) || OptOutModel.isNoOptoutLink(optoutLinkType)) {
    return {};
  }

  const optOuts =
    optoutLinkArr?.map((el) => {
      return {
        id: el.value,
        url: el.url,
        domain: el.domain,
      };
    }) || [];

  return {
    optOuts,
  };
};

export const parseSanitizingUnicode = (values) => {
  const { messageType, sanitizing } = values;
  const isSMS = CampaignModel.isSMS(messageType);

  return {
    sanitizingMessageUnicodeEnabled: !isSMS
      ? false
      : sanitizing === SanitizingTypeEnum.sanitizingMessageUnicodeEnabled,
    sanitizingTagUnicodeEnabled: !isSMS
      ? false
      : sanitizing === SanitizingTypeEnum.sanitizingTagUnicodeEnabled,
  };
};

export const parseEvaluatedElements = (values) => {
  return {
    evaluatedElements: values?.evaluatedElements?.map((el) => el.value),
  };
};

export const parseOptoutDTMFInput = (values) => {
  const isVoice = CampaignModel.isVoiceCampaign(values.messageType);

  return {
    optOutDtmfInput: isVoice ? (values?.optOutDtmfEnable ? values?.optOutDtmfInput : '') : '',
  };
};

export const parseDTMFTimeoutAndAMD = (values) => {
  const isVoice = CampaignModel.isVoiceCampaign(values.messageType);

  return {
    dtmfTimeout: isVoice ? values?.dtmfTimeout : '',
    amdEventEnabled: isVoice ? values?.amdEventEnabled : false,
  };
};

export const parseDebugMode = (values) => {
  const isVoiceCampaign = CampaignModel.isVoiceCampaign(values.messageType);

  return {
    debug: isVoiceCampaign ? values?.debug : false,
  };
};

export const parseAIAgent = (values) => {
  const isVoiceType = CampaignModel.isVoiceType(values.messageType);
  const agent = get(values, 'agent');

  return {
    agent: isVoiceType ? agent : false,
  };
};
